import { AppService } from '@services/app/app.service';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import logger from '@core/utils/logger';
import { Router } from '@angular/router';
import { PositionService } from '@services/position/position.service';
import { WizardService } from '@services/wizard/wizard.service';
import { ApiService } from '@core/modules/api/services/api/api.service';
import { LogLevel, LogRequestDTO } from '@core/modules/api/models';
import { AnalyticsService } from '@services/analytics/analytics.service';
import {
  BaseLog,
  ErrorLog,
  HttpReqLog,
  InfoLog,
  LogData,
  WarningLog
} from '@models/log';
import { DeviceService } from '../device/device.service';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private data: LogData[] = [];

  constructor(
    private appService: AppService,
    private positionService: PositionService,
    private wizardService: WizardService,
    private analyticsService: AnalyticsService,
    private apiService: ApiService,
    private deviceService: DeviceService,
    private route: Router
  ) {}

  addLogData(data: LogData[], options: { timeKey?: string } = {}): void {
    if (!(data instanceof Array)) {
      return;
    }

    data
      .filter(x => this.isValidData(x))
      .forEach(x => this.data.push({ ...x }));

    if (options.timeKey) {
      this.data.push({ key: options.timeKey, value: new Date().getTime() });
    }
  }

  async logError(error: any) {
    this.createErrorLog(error)
      .then(log => {
        try {
          this.sendLog('ERROR', log);

          logger.error(error);

          this.analyticsService.pushErrorEvent({
            errorType: log.type === 'http' ? 'backend' : 'generic',
            errorCode: log.code || null,
            errorMessage: log.message,
            errorField: null,
            isFatal: false
          });
        } catch (_) {}
      })
      .catch();
  }

  logWarning(message: any) {
    this.createWarningLog(message).then(log => {
      this.sendLog('WARN', log);
    });
  }

  logInfo(message: any) {
    this.createInfoLog(message).then(log => {
      this.sendLog('INFO', log);
    });
  }

  logHttpReq(message: HttpRequest<any>) {
    this.createHttpReqLog(message).then(log => {
      this.sendLog('INFO', log);
    });
  }

  private sendLog(level: LogLevel, log: ErrorLog): void {
    if (this.appService.getConfig().generic.sendLogs) {
      this.apiService.putLog({ body: this.convertLog(level, log) }).subscribe();
    }
  }

  private convertLog(level: LogLevel, log: BaseLog): LogRequestDTO {
    return {
      level,
      entityId: log.positionId,
      msg: this.toMessage(log)
    };
  }

  private async createInfoLog(message: any): Promise<InfoLog> {
    const baseLog = await this.createBaseLog();

    return {
      ...baseLog,
      message: this.toMessage(message)
    };
  }

  private async createWarningLog(warning: any): Promise<WarningLog> {
    const baseLog = await this.createBaseLog();

    return {
      ...baseLog,
      warning: this.toMessage(warning)
    };
  }

  private async createHttpReqLog(req: HttpRequest<any>): Promise<HttpReqLog> {
    const baseLog = await this.createBaseLog();

    return {
      ...baseLog,
      url: req.urlWithParams,
      responseType: req.responseType,
      method: req.method,
      body: req.body,
      headers: req.headers
        .keys()
        .filter(key => this.isAllowedLogHeader(key))
        .map(key => ({ key, value: req.headers.get(key) }))
    };
  }

  private async createErrorLog(error: any): Promise<ErrorLog> {
    if (error instanceof Error) {
      return this.createErrorLogFromError(error);
    } else if (error instanceof HttpErrorResponse) {
      return this.createErrorLogFromHttpError(error);
    } else if (typeof error === 'string') {
      return this.createErrorLogFromString(error);
    } else {
      return this.createErrorLogFromUnhandled();
    }
  }

  private async createErrorLogFromError(error: Error): Promise<ErrorLog> {
    const baseLog = await this.createBaseLog();

    return {
      ...baseLog,
      type: 'js',
      message: error.message,
      stack: error.stack,
      code: error.name
    };
  }

  private async createErrorLogFromString(error: string): Promise<ErrorLog> {
    const baseLog = await this.createBaseLog();

    return {
      ...baseLog,
      type: 'js',
      message: error
    };
  }

  private async createErrorLogFromHttpError(
    error: HttpErrorResponse
  ): Promise<ErrorLog> {
    const message =
      error.error && error.error.messages && error.error.messages[0];

    const baseLog = await this.createBaseLog();

    return {
      ...baseLog,
      type: 'http',
      message: (message && message.message) || error.message,
      uuid: (message && message.uuid) || '',
      corrId: error.headers.get('qinetic-correlation-id'),
      status: error.status,
      url: error.url,
      code: error.name
    };
  }

  private async createErrorLogFromUnhandled(): Promise<ErrorLog> {
    const baseLog = await this.createBaseLog();

    return {
      ...baseLog,
      type: 'js',
      message: 'Unhandled error'
    };
  }

  private async createBaseLog(): Promise<BaseLog> {
    const activeStep = this.wizardService.getActiveStep();

    const deviceInfo = await this.deviceService.getDeviceInfo();

    const output: BaseLog = {
      appVersion: {
        container: this.appService.getAppVersion('container'),
        core: this.appService.getAppVersion('core')
      },
      device: {
        model: deviceInfo.model,
        operatingSystem: deviceInfo.operatingSystem,
        osVersion: deviceInfo.osVersion,
        manufacturer: deviceInfo.manufacturer,
        name: deviceInfo.name
      },
      userAgent: navigator.userAgent,
      time: new Date().getTime(),
      page: this.route.url,
      positionId: this.positionService.getPositionId(),
      wizardCode: this.wizardService.getWizardCode(),
      stepCode: activeStep && activeStep.stepCode,
      data: this.useData()
    };

    if (this.deviceService.isNative()) {
      const appInfo = await this.appService.getAppInfo();

      output.appVersion.native = appInfo.version;
    }

    return output;
  }

  private useData(): LogData[] {
    const data = [...this.data];

    this.data = [];

    return data;
  }

  private toMessage(obj: any): string {
    let msg = '';

    if (typeof obj === 'object' && obj !== null) {
      try {
        msg = JSON.stringify(obj);
      } catch (_) {}
    } else if (typeof obj === 'string') {
      msg = obj;
    }

    return msg;
  }

  private isValidData(data: LogData): boolean {
    return (
      !!data &&
      typeof data.key === 'string' &&
      (typeof data.value == 'string' ||
        typeof data.value == 'number' ||
        typeof data.value == 'boolean')
    );
  }

  private isAllowedLogHeader(key: string): boolean {
    const authHeaderName = this.appService.getConfig().security.authHeaderName;

    return (
      !authHeaderName || authHeaderName.toLowerCase() !== key.toLowerCase()
    );
  }
}
